import { useState, useEffect } from 'react';
import { Save, Image, Upload, Trash2, Plus, GripVertical, ChevronUp, ChevronDown, Video, RefreshCw, CheckCircle2, AlertTriangle, Pencil } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import MediaUpload from '../../components/MediaUpload';
import LandingPagesContent from './LandingPagesContent';
import { categoryList, itemKeysInCategory, validateRename, renameCategory, removeCategoryFromOrder, addCategory } from './salesGalleryCategories';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Section Component for reusability
const ContentSection = ({ title, children }) => (
  <div className="bg-white rounded-xl p-6 shadow-soft">
    <h3 className="font-display text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
      <Image className="w-5 h-5 text-emerald-600" />
      {title}
    </h3>
    {children}
  </div>
);

const ImageField = ({ label, value, onChange, onUpload, altValue, onAltChange }) => {
  const isVideoUrl = (url) => {
    if (!url) return false;
    return url.match(/\.(mp4|webm|mov)$/i) || url.includes('/video/upload/');
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={value} onChange={onChange} placeholder="https://..." className="flex-1" />
        <Button type="button" variant="outline" onClick={onUpload}>
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </Button>
      </div>
      {value && (
        <div className="mt-2">
          <Label className="text-sm text-gray-600">Alt Text (untuk aksesibilitas)</Label>
          <Input
            value={altValue || ''}
            onChange={onAltChange}
            placeholder="Deskripsi gambar..."
            className="mt-1"
          />
          {isVideoUrl(value) ? (
            <video src={value} controls className="mt-2 rounded-lg h-24 object-cover" />
          ) : (
            <img src={value} alt={altValue || 'Preview'} className="mt-2 rounded-lg h-24 object-cover" />
          )}
        </div>
      )}
    </div>
  );
};

const SortableGalleryItem = ({ id, sectionName, contentKey, content, getContent, updateField, openMediaUpload, saveContent, deleteContent, isSaving, setEditingKey, categories }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const handleOpenChange = (open) => {
    setIsExpanded(open);
    if (!open) {
      setIsNewCategory(false);
      setNewCategoryName('');
    }
    setEditingKey(open ? contentKey : null);
  };
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isExpanded ? 50 : 1,
  };
  const currentImage = getContent('sales_gallery', sectionName, 'image');

  const isVideoUrl = (url) => {
    if (!url) return false;
    return url.match(/\.(mp4|webm|mov)$/i) || url.includes('/video/upload/');
  };

  return (
    <>
      <div 
        ref={setNodeRef} 
        style={style} 
        {...attributes} 
        {...listeners}
        onClick={() => setIsExpanded(true)}
        className="relative bg-gray-100 rounded-lg border shadow-sm flex flex-col aspect-square overflow-hidden cursor-pointer hover:ring-2 hover:ring-emerald-500 transition-all group"
      >
        <div className="absolute top-2 left-2 z-10 bg-black/60 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center backdrop-blur-sm shadow-sm">
          {getContent('sales_gallery', sectionName, 'order', 0) + 1}
        </div>
        
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 z-0 transition-all flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 bg-white text-gray-900 text-xs font-medium px-2 py-1 rounded shadow-sm pointer-events-none">Edit</span>
        </div>

        {currentImage ? (
           isVideoUrl(currentImage) ? (
             <video src={`${currentImage}#t=0.1`} className="w-full h-full object-cover" />
           ) : (
             <img src={currentImage} className="w-full h-full object-cover" />
           )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400"><Image className="w-8 h-8" /></div>
        )}
      </div>

      <Dialog open={isExpanded} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Sales Gallery Item</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 mt-4">
            <ImageField
              label="Media (URL)"
              value={currentImage}
              onChange={(e) => updateField(contentKey, 'image', e.target.value)}
              onUpload={() => openMediaUpload(contentKey, 'image', 'auto')}
            />
            <div className="grid grid-cols-2 gap-4">
               <div>
                 <Label>Judul</Label>
                 <Input 
                   value={getContent('sales_gallery', sectionName, 'title')}
                   onChange={(e) => updateField(contentKey, 'title', e.target.value)}
                   placeholder="Judul"
                 />
               </div>
               <div>
                 <Label>Kategori</Label>
                 <div className="flex flex-col gap-2">
                   <select
                     value={isNewCategory ? '__new__' : (getContent('sales_gallery', sectionName, 'category') || 'Umum')}
                     onChange={(e) => {
                       if (e.target.value === '__new__') {
                         setIsNewCategory(true);
                         setNewCategoryName('');
                       } else {
                         setIsNewCategory(false);
                         updateField(contentKey, 'category', e.target.value);
                       }
                     }}
                     className="border border-gray-300 rounded px-2 py-1 text-sm bg-white h-9"
                   >
                     {Array.from(new Set([
                       ...(categories || []),
                       getContent('sales_gallery', sectionName, 'category'),
                     ].filter(Boolean))).map(cat => (
                       <option key={cat} value={cat}>{cat}</option>
                     ))}
                     <option value="__new__">＋ Kategori baru…</option>
                   </select>
                   {isNewCategory && (
                     <Input
                       value={newCategoryName}
                       onChange={(e) => setNewCategoryName(e.target.value)}
                       placeholder="Nama kategori baru"
                     />
                   )}
                 </div>
               </div>
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea
                value={getContent('sales_gallery', sectionName, 'description')}
                onChange={(e) => updateField(contentKey, 'description', e.target.value)}
                placeholder="Deskripsi detail..."
                rows={3}
              />
            </div>
            <div className="flex justify-between items-center mt-4 pt-4 border-t">
               <Button
                 onClick={() => {
                   deleteContent('sales_gallery', sectionName);
                   handleOpenChange(false);
                 }}
                 disabled={isSaving}
                 variant="destructive"
                 className="flex items-center gap-2"
               >
                 <Trash2 className="w-4 h-4" /> Hapus
               </Button>
               <div className="flex gap-2">
                 <Button variant="outline" onClick={() => handleOpenChange(false)}>Tutup</Button>
                 <Button
                   onClick={() => {
                     const base = content[contentKey]?.content || {};
                     const chosen = isNewCategory ? newCategoryName : (base.category || 'Umum');
                     if (!chosen.trim()) {
                       toast.error('Nama kategori tidak boleh kosong');
                       return;
                     }
                     const toSave = { ...base, category: chosen.trim() };
                     updateField(contentKey, 'category', toSave.category);
                     saveContent('sales_gallery', sectionName, 'image', toSave);
                     handleOpenChange(false);
                   }}
                   disabled={isSaving}
                   className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                 >
                   <Save className="w-4 h-4" /> Simpan
                 </Button>
               </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CategorySortableItem = ({ id, index, count, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(id);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const commitRename = () => {
    setIsEditing(false);
    if (draft.trim() && draft.trim() !== id) onRename(draft.trim());
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center gap-3 bg-white border rounded-lg p-3 cursor-grab hover:border-emerald-500 hover:shadow-sm transition-all"
    >
      <GripVertical className="text-gray-400 w-5 h-5" />
      <span className="font-medium text-gray-700 w-8">{index + 1}.</span>
      {isEditing ? (
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          placeholder="Nama kategori"
          className="flex-1 h-8"
        />
      ) : (
        <span className="font-semibold text-gray-900 flex-1">{id}</span>
      )}
      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full whitespace-nowrap">
        {count} item
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => {
          setDraft(id);
          setIsEditing(true);
        }}
        aria-label={`Rename kategori ${id}`}
      >
        <Pencil className="w-4 h-4" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onDelete}
        aria-label={`Hapus kategori ${id}`}
      >
        <Trash2 className="w-4 h-4 text-red-500" />
      </Button>
    </div>
  );
};


const ContentManagement = () => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [content, setContent] = useState({});
  const [allRooms, setAllRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [siteMinderStatus, setSiteMinderStatus] = useState(null);
  const [isSyncingSiteMinder, setIsSyncingSiteMinder] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  // Special Offers state
  const [offers, setOffers] = useState([]);
  const [editingOffer, setEditingOffer] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);

  // Facilities state
  const [facilities, setFacilities] = useState([]);
  const [editingFacility, setEditingFacility] = useState(null);
  const [showFacilityModal, setShowFacilityModal] = useState(false);

  // Sales Gallery Pagination state
  const [salesItemsPerPage, setSalesItemsPerPage] = useState(5);
  const [salesCurrentPage, setSalesCurrentPage] = useState(1);
  const [salesCategoryFilter, setSalesCategoryFilter] = useState('Semua');
  const [newSalesCategory, setNewSalesCategory] = useState('');
  // Track the item whose edit dialog is open so the filter never unmounts it mid-edit
  const [editingSalesKey, setEditingSalesKey] = useState(null);

  const getToken = () => localStorage.getItem('token');
  // Filter for Sales Gallery: show matching category OR the item being edited
  const matchesSalesFilter = (key) =>
    key.startsWith('sales_gallery_item_') &&
    (salesCategoryFilter === 'Semua' ||
      content[key]?.content?.category === salesCategoryFilter ||
      key === editingSalesKey);

  // Daftar kategori permanen: entri config urutan dipertahankan meski tanpa item,
  // lalu kategori item yang belum tersimpan (lihat salesGalleryCategories).
  const savedOrderArr = content['sales_gallery_category_order']?.content?.order || [];
  const displayCats = categoryList(savedOrderArr, content);
  const categoryCounts = displayCats.reduce((acc, cat) => {
    acc[cat] = itemKeysInCategory(content, cat).length;
    return acc;
  }, {});

  // Tambah item baru lalu lompat ke halaman/kategori tempat item itu muncul,
  // supaya tombol terasa merespons (item tidak tersembunyi di halaman belakang).
  const addSalesItem = () => {
    const section = `item_${Date.now()}`;
    const key = `sales_gallery_${section}`;
    const existingKeys = Object.keys(content).filter(k => k.startsWith('sales_gallery_item_'));
    const category = salesCategoryFilter !== 'Semua' ? salesCategoryFilter : 'Umum';
    const sameCat = existingKeys.filter(
      k => (content[k]?.content?.category || 'Umum') === category
    ).length;

    setContent(prev => ({
      ...prev,
      [key]: {
        page: 'sales_gallery',
        section,
        content_type: 'image',
        content: { image: '', title: '', description: '', category, order: existingKeys.length }
      }
    }));
    setSalesCurrentPage(Math.max(1, Math.ceil((sameCat + 1) / salesItemsPerPage)));
  };

  const addSalesCategory = async () => {
    const { content: nextContent, error } = addCategory(content, newSalesCategory);
    if (error) {
      toast.error(error);
      return;
    }
    setNewSalesCategory('');
    await saveContent('sales_gallery', 'category_order', 'config', nextContent.sales_gallery_category_order.content);
  };

  const isBulkUpload = uploadTarget?.key === 'sales_gallery_bulk';

  useEffect(() => {
    fetchContent();
    fetchRooms();
    fetchOffers();
    fetchFacilities();
    fetchSiteMinderStatus();
  }, []);

  const fetchSiteMinderStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/siteminder/status`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSiteMinderStatus(response.data);
    } catch (error) {
      setSiteMinderStatus({ connected: false, message: 'Gagal memeriksa koneksi SiteMinder' });
    }
  };

  const syncSiteMinder = async () => {
    setIsSyncingSiteMinder(true);
    try {
      const response = await axios.post(`${API_URL}/admin/siteminder/sync`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(`${response.data.mapped}/${response.data.total} kamar berhasil dipetakan ke SiteMinder`);
      await fetchSiteMinderStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyinkronkan SiteMinder');
    } finally {
      setIsSyncingSiteMinder(false);
    }
  };

  // Fetch facilities
  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/facilities`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setFacilities(response.data);
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const saveFacility = async (facilityData) => {
    setIsSaving(true);
    try {
      if (editingFacility?.id) {
        await axios.put(`${API_URL}/admin/facilities/${editingFacility.id}`, facilityData, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Fasilitas berhasil diupdate');
      } else {
        await axios.post(`${API_URL}/admin/facilities`, facilityData, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Fasilitas berhasil ditambahkan');
      }
      fetchFacilities();
      setShowFacilityModal(false);
      setEditingFacility(null);
    } catch (error) {
      toast.error('Gagal menyimpan fasilitas');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteFacility = async (fid) => {
    if (!window.confirm('Hapus fasilitas ini?')) return;
    setIsSaving(true);
    try {
      await axios.delete(`${API_URL}/admin/facilities/${fid}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Fasilitas berhasil dihapus');
      fetchFacilities();
    } catch (error) {
      toast.error('Gagal menghapus fasilitas');
    } finally {
      setIsSaving(false);
    }
  };

  // Fetch special offers
  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API_URL}/special-offers`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const saveOffer = async (offerData) => {
    setIsSaving(true);
    try {
      if (editingOffer?.id) {
        await axios.put(`${API_URL}/admin/special-offers/${editingOffer.id}`, offerData, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Offer berhasil diupdate');
      } else {
        await axios.post(`${API_URL}/admin/special-offers`, offerData, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        toast.success('Offer berhasil ditambahkan');
      }
      fetchOffers();
      setShowOfferModal(false);
      setEditingOffer(null);
    } catch (error) {
      toast.error('Gagal menyimpan offer');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Hapus offer ini?')) return;
    setIsSaving(true);
    try {
      await axios.delete(`${API_URL}/admin/special-offers/${offerId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Offer berhasil dihapus');
      fetchOffers();
    } catch (error) {
      toast.error('Gagal menghapus offer');
    } finally {
      setIsSaving(false);
    }
  };

  // Reorder special offers
  const reorderOffer = async (offerId, direction) => {
    const sorted = [...offers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const idx = sorted.findIndex(o => o.id === offerId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const current = sorted[idx];
    const target = sorted[swapIdx];

    try {
      await Promise.all([
        axios.put(`${API_URL}/admin/special-offers/${current.id}`, { order: target.order ?? swapIdx }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.put(`${API_URL}/admin/special-offers/${target.id}`, { order: current.order ?? idx }, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
      ]);
      fetchOffers();
    } catch (error) {
      toast.error('Gagal mengubah urutan');
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/content`);
      const contentMap = {};
      response.data.forEach(item => {
        const key = `${item.page}_${item.section}`;
        contentMap[key] = item;
      });
      setContent(contentMap);
    } catch (error) {
      toast.error('Error fetching content');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/rooms`);
      setAllRooms(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // keep silent to not block CMS
    }
  };

  const saveContent = async (page, section, contentType, contentData) => {
    setIsSaving(true);
    try {
      await axios.post(`${API_URL}/admin/content`, {
        page,
        section,
        content_type: contentType,
        content: contentData
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Konten berhasil disimpan');
      fetchContent();
    } catch (error) {
      toast.error('Gagal menyimpan konten');
    } finally {
      setIsSaving(false);
    }
  };

  // Save all content for a specific page
  const saveAllForPage = async (page) => {
    setIsSaving(true);
    const sectionsToSave = Object.keys(content)
      .filter(key => key.startsWith(`${page}_`))
      .map(key => {
        const section = key.replace(`${page}_`, '');
        // Preserve existing content_type if available; otherwise determine based on section name
        const contentType = content[key]?.content_type || 
          (section.startsWith('gallery_item_') ? 'image' : (section === 'hero' ? 'hero' : 'section'));
        return { 
          section, 
          content_type: contentType,
          content: content[key]?.content 
        };
      })
      .filter(item => item.content && Object.keys(item.content).length > 0);

    if (sectionsToSave.length === 0) {
      toast.info('Tidak ada perubahan untuk disimpan');
      setIsSaving(false);
      return;
    }

    try {
      await Promise.all(sectionsToSave.map(item =>
        axios.post(`${API_URL}/admin/content`, {
          page,
          section: item.section,
          content_type: item.content_type,
          content: item.content
        }, { headers: { Authorization: `Bearer ${getToken()}` } })
      ));
      toast.success(`Semua konten halaman ${page} berhasil disimpan (${sectionsToSave.length} section)`);
      fetchContent();
    } catch (error) {
      toast.error('Gagal menyimpan beberapa konten');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (key, field, value) => {
    setContent(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        content: {
          ...prev[key]?.content,
          [field]: value
        }
      }
    }));
  };

  const getContent = (page, section, field, defaultValue = '') => {
    const key = `${page}_${section}`;
    return content[key]?.content?.[field] ?? defaultValue;
  };

  const openMediaUpload = (key, field, mediaType = 'image', roomOverrideId = null) => {
    setUploadTarget({ key, field, mediaType, roomOverrideId });
    setShowMediaUpload(true);
  };

  const handleMediaUpload = (mediaData) => {
    if (uploadTarget) {
      if (uploadTarget.key === 'sales_gallery_bulk') {
         const newItems = Array.isArray(mediaData) ? mediaData : [mediaData];
         const category = salesCategoryFilter !== 'Semua' ? salesCategoryFilter : 'Umum';
         const sameCat = Object.keys(content).filter(
           k => k.startsWith('sales_gallery_item_') && (content[k]?.content?.category || 'Umum') === category
         ).length;

         setContent(prev => {
            const next = { ...prev };
            let lastOrder = Object.keys(prev).filter(k => k.startsWith('sales_gallery_item_')).length;
            newItems.forEach((item, index) => {
                const timestamp = new Date().getTime() + index;
                const section = `item_${timestamp}`;
                const key = `sales_gallery_${section}`;
                next[key] = {
                   page: 'sales_gallery',
                   section: section,
                   content_type: 'image',
                   content: {
                       image: item.secure_url,
                       title: '',
                       description: '',
                       category,
                       order: lastOrder + index
                   }
                };
            });
            return next;
         });
         setSalesCurrentPage(Math.max(1, Math.ceil((sameCat + newItems.length) / salesItemsPerPage)));
         toast.success(`${newItems.length} media berhasil ditambahkan ke Sales Gallery`);
      } else if (uploadTarget.context === 'offer') {
        setEditingOffer(prev => ({ ...prev, image: mediaData.secure_url }));
      } else if (uploadTarget.context === 'offer_promo') {
        setEditingOffer(prev => ({ ...prev, promo_media_url: mediaData.secure_url }));
      } else if (uploadTarget.context === 'facility') {
        setEditingFacility(prev => ({ ...prev, image: mediaData.secure_url }));
      } else if (uploadTarget.roomOverrideId) {
        const imageUrl = mediaData.url || mediaData.secure_url;
        setContent(prev => {
          const prevItem = prev[uploadTarget.key] || {};
          const prevContent = prevItem.content || {};
          const prevOverrides = prevContent.overrides || {};
          const prevOverride = prevOverrides[uploadTarget.roomOverrideId] || {};
          return {
            ...prev,
            [uploadTarget.key]: {
              ...prevItem,
              content: {
                ...prevContent,
                overrides: {
                  ...prevOverrides,
                  [uploadTarget.roomOverrideId]: {
                    ...prevOverride,
                    image: imageUrl
                  }
                }
              }
            }
          };
        });
        toast.success('Media berhasil diupload');
      } else {
        updateField(uploadTarget.key, uploadTarget.field, mediaData.secure_url);
        toast.success('Media berhasil diupload');
      }
      setShowMediaUpload(false);
      setUploadTarget(null);
    }
  };

  const deleteContent = async (page, section) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    setIsSaving(true);
    try {
      await axios.delete(`${API_URL}/admin/content/${page}/${section}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Konten berhasil dihapus');

      // Update local state
      setContent(prev => {
        const newContent = { ...prev };
        delete newContent[`${page}_${section}`];
        return newContent;
      });
    } catch (error) {
      toast.error('Gagal menghapus konten');
    } finally {
      setIsSaving(false);
    }
  };

  const renameSalesCategory = async (oldName, rawNewName) => {
    const error = validateRename(displayCats, oldName, rawNewName);
    if (error) {
      toast.error(error);
      return;
    }
    const clean = rawNewName.trim();
    if (clean === oldName) return;
    const { content: next, renamedKeys } = renameCategory(content, oldName, clean);
    setContent(next);
    if (salesCategoryFilter === oldName) setSalesCategoryFilter(clean);
    setIsSaving(true);
    try {
      const posts = renamedKeys.map((key) =>
        axios.post(`${API_URL}/admin/content`, {
          page: 'sales_gallery',
          section: key.replace('sales_gallery_', ''),
          content_type: next[key]?.content_type || 'image',
          content: next[key].content,
        }, { headers: { Authorization: `Bearer ${getToken()}` } })
      );
      posts.push(
        axios.post(`${API_URL}/admin/content`, {
          page: 'sales_gallery',
          section: 'category_order',
          content_type: 'config',
          content: next.sales_gallery_category_order.content,
        }, { headers: { Authorization: `Bearer ${getToken()}` } })
      );
      await Promise.all(posts);
      toast.success(`Kategori "${oldName}" menjadi "${clean}"`);
      fetchContent();
    } catch (error) {
      toast.error('Gagal menyimpan rename kategori');
      fetchContent();
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSalesCategory = async (category) => {
    const count = itemKeysInCategory(content, category).length;
    if (count > 0) {
      toast.error(`Kategori "${category}" masih berisi ${count} item — pindahkan dulu itemnya ke kategori lain`);
      return;
    }
    if (!window.confirm(`Hapus kategori "${category}"?`)) return;
    const { content: next } = removeCategoryFromOrder(content, category);
    setContent(next);
    if (salesCategoryFilter === category) setSalesCategoryFilter('Semua');
    await saveContent('sales_gallery', 'category_order', 'config', {
      order: next.sales_gallery_category_order?.content?.order || [],
    });
  };

  const addGalleryItem = () => {
    const timestamp = new Date().getTime();
    const section = `gallery_item_${timestamp}`;
    const key = `gallery_${section}`;
    updateField(key, 'url', '');
    updateField(key, 'caption', '');
    updateField(key, 'category', 'general');
    updateField(key, 'order', Object.keys(content).filter(k => k.startsWith('gallery_gallery_item_')).length);
  };

  // Sort gallery items by order
  const getGalleryItems = () => {
    return Object.keys(content)
      .filter(key => key.startsWith('gallery_gallery_item_'))
      .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="content-management">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900">Kelola Konten</h1>
          <p className="text-gray-500">Edit semua teks, foto, dan video di setiap halaman</p>
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            if (!window.confirm("Perbaiki duplikasi konten? Ini akan menghapus versi lama jika ada duplikat.")) return;
            try {
              const res = await axios.post(`${API_URL}/admin/content/fix-duplicates`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` }
              });
              toast.success(`Deduplikasi: ${res.data.stats.merged} merged, ${res.data.stats.deleted} deleted`);
              fetchContent();
            } catch (e) {
              toast.error("Gagal memperbaiki duplikat");
            }
          }}
        >
          Fix Duplicates
        </Button>
      </div>

      <Tabs defaultValue="home" className="space-y-6">
        <TabsList className="bg-white shadow-soft flex-wrap h-auto p-1">
          <TabsTrigger value="home">Home</TabsTrigger>
          <TabsTrigger value="rooms">Rooms Page</TabsTrigger>
          <TabsTrigger value="family_hotel">Landing Keluarga</TabsTrigger>
          <TabsTrigger value="selecta">Landing Selecta</TabsTrigger>
          <TabsTrigger value="mountain_view">Landing Mountain View</TabsTrigger>
          <TabsTrigger value="corporate_group">Landing Corporate & Group</TabsTrigger>
          <TabsTrigger value="meeting">Meeting</TabsTrigger>
          <TabsTrigger value="wedding">Wedding</TabsTrigger>
          <TabsTrigger value="facilities">Facilities</TabsTrigger>
          <TabsTrigger value="gallery">Gallery</TabsTrigger>
          <TabsTrigger value="sales_gallery">Sales Gallery</TabsTrigger>
          <TabsTrigger value="restoran">Kenari Restaurant</TabsTrigger>
          <TabsTrigger value="pool_cafe">Rintik Rindu Pool Cafe</TabsTrigger>
          <TabsTrigger value="footer">Footer</TabsTrigger>
          <TabsTrigger value="offers">Special Offers</TabsTrigger>
          <TabsTrigger value="table-manner">Table Manner</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* ==================== TARGETED LANDING PAGES ==================== */}
        <LandingPagesContent
          getContent={getContent}
          updateField={updateField}
          saveContent={saveContent}
          saveAllForPage={saveAllForPage}
          openMediaUpload={openMediaUpload}
          isSaving={isSaving}
          content={content}
          ContentSection={ContentSection}
          ImageField={ImageField}
          allRooms={allRooms}
        />

        {/* ==================== HOME PAGE ==================== */}
        <TabsContent value="home" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Home</span>
            <Button onClick={() => saveAllForPage('home')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>
          {/* Hero Section */}
          <ContentSection title="Hero Section">
            <div className="space-y-4">
              <div>
                <Label>Judul Utama</Label>
                <Input
                  value={getContent('home', 'hero', 'title')}
                  onChange={(e) => updateField('home_hero', 'title', e.target.value)}
                  placeholder="Spencer Green Hotel"
                  data-testid="home-hero-title"
                />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input
                  value={getContent('home', 'hero', 'subtitle')}
                  onChange={(e) => updateField('home_hero', 'subtitle', e.target.value)}
                  placeholder="Experience Luxury..."
                  data-testid="home-hero-subtitle"
                />
              </div>
              <ImageField
                label="Gambar Hero"
                value={getContent('home', 'hero', 'image')}
                onChange={(e) => updateField('home_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('home_hero', 'image')}
                altValue={getContent('home', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('home_hero', 'imageAlt', e.target.value)}
              />
              <div>
                <Label>Video URL Hero (opsional - jika diisi, hero akan auto-play video)</Label>
                <Input
                  value={getContent('home', 'hero', 'video')}
                  onChange={(e) => updateField('home_hero', 'video', e.target.value)}
                  placeholder="https://youtube.com/watch?v=... atau link .mp4"
                  data-testid="home-hero-video"
                />
                <p className="text-xs text-gray-500 mt-1">Mendukung YouTube dan link video langsung (.mp4, .webm). Kosongkan untuk tampilkan gambar saja.</p>
                {getContent('home', 'hero', 'video') && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-emerald-600 font-medium">✓ Video aktif</span>
                    <Button variant="outline" size="sm" onClick={() => updateField('home_hero', 'video', '')}>
                      <Trash2 className="w-3 h-3 mr-1" />Hapus Video
                    </Button>
                  </div>
                )}
              </div>
              <Button onClick={() => saveContent('home', 'hero', 'hero', content['home_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
            </div>
          </ContentSection>

          {/* Promo Banner */}
          <ContentSection title="Promo Banner">
            <div className="space-y-4">
              <div>
                <Label>Judul Promo</Label>
                <Input value={getContent('home', 'promo_banner', 'title')} onChange={(e) => updateField('home_promo_banner', 'title', e.target.value)} placeholder="Special Offer" />
              </div>
              <div>
                <Label>Deskripsi Promo</Label>
                <Textarea value={getContent('home', 'promo_banner', 'description')} onChange={(e) => updateField('home_promo_banner', 'description', e.target.value)} rows={2} />
              </div>
              <ImageField
                label="Gambar Promo"
                value={getContent('home', 'promo_banner', 'image')}
                onChange={(e) => updateField('home_promo_banner', 'image', e.target.value)}
                onUpload={() => openMediaUpload('home_promo_banner', 'image')}
                altValue={getContent('home', 'promo_banner', 'imageAlt')}
                onAltChange={(e) => updateField('home_promo_banner', 'imageAlt', e.target.value)}
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={getContent('home', 'promo_banner', 'is_active', false)}
                  onCheckedChange={(v) => updateField('home_promo_banner', 'is_active', v)}
                />
                <Label>Tampilkan Promo</Label>
              </div>
              <Button onClick={() => saveContent('home', 'promo_banner', 'banner', content['home_promo_banner']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Promo
              </Button>
            </div>
          </ContentSection>

          {/* About Section */}
          {/* About Section - Disabled temporarily
          <ContentSection title="About Section">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('home', 'about', 'title')} onChange={(e) => updateField('home_about', 'title', e.target.value)} placeholder="About Our Hotel" />
              </div>
              <div>
                <Label>Deskripsi</Label>
                <Textarea value={getContent('home', 'about', 'description')} onChange={(e) => updateField('home_about', 'description', e.target.value)} rows={4} />
              </div>
              <ImageField
                label="Gambar"
                value={getContent('home', 'about', 'image')}
                onChange={(e) => updateField('home_about', 'image', e.target.value)}
                onUpload={() => openMediaUpload('home_about', 'image')}
                altValue={getContent('home', 'about', 'imageAlt')}
                onAltChange={(e) => updateField('home_about', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('home', 'about', 'section', content['home_about']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan About
              </Button>
            </div>
          </ContentSection>
          */}
        </TabsContent>

        {/* ==================== ROOMS PAGE ==================== */}
        <TabsContent value="rooms" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Rooms</span>
            <Button onClick={() => saveAllForPage('rooms')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>
          <ContentSection title="Rooms Page Hero">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('rooms', 'hero', 'title')} onChange={(e) => updateField('rooms_hero', 'title', e.target.value)} placeholder="Kamar Kami" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('rooms', 'hero', 'subtitle')} onChange={(e) => updateField('rooms_hero', 'subtitle', e.target.value)} placeholder="Akomodasi" />
              </div>
              <ImageField
                label="Background Image"
                value={getContent('rooms', 'hero', 'image')}
                onChange={(e) => updateField('rooms_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('rooms_hero', 'image')}
                altValue={getContent('rooms', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('rooms_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('rooms', 'hero', 'hero', content['rooms_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan
              </Button>
            </div>
          </ContentSection>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-sm">
              <strong>Tips:</strong> Untuk edit detail kamar (nama, harga, foto, video), gunakan menu <strong>Kelola Kamar</strong> di sidebar.
            </p>
          </div>
        </TabsContent>

        {/* ==================== MEETING PAGE ==================== */}
        <TabsContent value="meeting" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Meeting</span>
            <Button onClick={() => saveAllForPage('meeting')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>
          <ContentSection title="Meeting Page Hero">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('meeting', 'hero', 'title')} onChange={(e) => updateField('meeting_hero', 'title', e.target.value)} placeholder="Meeting & Events" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('meeting', 'hero', 'subtitle')} onChange={(e) => updateField('meeting_hero', 'subtitle', e.target.value)} placeholder="Corporate Events" />
              </div>
              <div>
                <Label>Deskripsi Hero</Label>
                <Textarea value={getContent('meeting', 'hero', 'description')} onChange={(e) => updateField('meeting_hero', 'description', e.target.value)} placeholder="Host your next corporate event..." rows={2} />
              </div>
              <ImageField
                label="Background Image"
                value={getContent('meeting', 'hero', 'image')}
                onChange={(e) => updateField('meeting_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('meeting_hero', 'image')}
                altValue={getContent('meeting', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('meeting_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('meeting', 'hero', 'hero', content['meeting_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
            </div>
          </ContentSection>

          {/* Meeting Rooms */}
          {[1, 2].map((num) => (
            <ContentSection key={`room${num}`} title={`Meeting Room ${num}`}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nama Ruangan</Label>
                    <Input value={getContent('meeting', `room${num}`, 'name')} onChange={(e) => updateField(`meeting_room${num}`, 'name', e.target.value)} placeholder="Boardroom" />
                  </div>
                  <div>
                    <Label>Kapasitas</Label>
                    <Input value={getContent('meeting', `room${num}`, 'capacity')} onChange={(e) => updateField(`meeting_room${num}`, 'capacity', e.target.value)} placeholder="10 - 20 pax" />
                  </div>
                </div>
                <div>
                  <Label>Fitur (pisahkan dengan koma)</Label>
                  <Input value={getContent('meeting', `room${num}`, 'features')} onChange={(e) => updateField(`meeting_room${num}`, 'features', e.target.value)} placeholder="4K Display, Video Conference, Whiteboard" />
                </div>
                <ImageField
                  label="Gambar"
                  value={getContent('meeting', `room${num}`, 'image')}
                  onChange={(e) => updateField(`meeting_room${num}`, 'image', e.target.value)}
                  onUpload={() => openMediaUpload(`meeting_room${num}`, 'image')}
                  altValue={getContent('meeting', `room${num}`, 'imageAlt')}
                  onAltChange={(e) => updateField(`meeting_room${num}`, 'imageAlt', e.target.value)}
                />
                <Button onClick={() => saveContent('meeting', `room${num}`, 'room', content[`meeting_room${num}`]?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Save className="w-4 h-4 mr-2" />Simpan Room {num}
                </Button>
              </div>
            </ContentSection>
          ))}

          {/* Meeting Packages */}
          {[1, 2, 3].map((num) => (
            <ContentSection key={`pkg${num}`} title={`Meeting Package ${num}`}>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Nama Paket</Label>
                    <Input value={getContent('meeting', `package${num}`, 'name')} onChange={(e) => updateField(`meeting_package${num}`, 'name', e.target.value)} placeholder="Half Day" />
                  </div>
                  <div>
                    <Label>Durasi</Label>
                    <Input value={getContent('meeting', `package${num}`, 'duration')} onChange={(e) => updateField(`meeting_package${num}`, 'duration', e.target.value)} placeholder="4 Hours" />
                  </div>
                  <div>
                    <Label>Harga</Label>
                    <Input value={getContent('meeting', `package${num}`, 'price')} onChange={(e) => updateField(`meeting_package${num}`, 'price', e.target.value)} placeholder="Rp 500.000/pax" />
                  </div>
                </div>
                <div>
                  <Label>Termasuk (pisahkan dengan baris baru)</Label>
                  <Textarea value={getContent('meeting', `package${num}`, 'includes')} onChange={(e) => updateField(`meeting_package${num}`, 'includes', e.target.value)} rows={3} placeholder="1x Coffee Break&#10;Meeting Room&#10;WiFi" />
                </div>
                <Button onClick={() => saveContent('meeting', `package${num}`, 'package', content[`meeting_package${num}`]?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Save className="w-4 h-4 mr-2" />Simpan Package {num}
                </Button>
              </div>
            </ContentSection>
          ))}

          {/* Meeting CTA */}
          <ContentSection title="CTA Section (Bawah)">
            <div className="space-y-4">
              <div>
                <Label>Judul CTA</Label>
                <Input value={getContent('meeting', 'cta', 'title')} onChange={(e) => updateField('meeting_cta', 'title', e.target.value)} placeholder="Plan Your Next Event" />
              </div>
              <div>
                <Label>Deskripsi CTA</Label>
                <Textarea value={getContent('meeting', 'cta', 'description')} onChange={(e) => updateField('meeting_cta', 'description', e.target.value)} rows={2} placeholder="Our dedicated events team..." />
              </div>
              <Button onClick={() => saveContent('meeting', 'cta', 'cta', content['meeting_cta']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan CTA
              </Button>
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== WEDDING PAGE ==================== */}
        <TabsContent value="wedding" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Wedding</span>
            <Button onClick={() => saveAllForPage('wedding')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>
          <ContentSection title="Wedding Page Hero">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('wedding', 'hero', 'title')} onChange={(e) => updateField('wedding_hero', 'title', e.target.value)} placeholder="Your Dream Wedding" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('wedding', 'hero', 'subtitle')} onChange={(e) => updateField('wedding_hero', 'subtitle', e.target.value)} placeholder="Create unforgettable memories..." />
              </div>
              <ImageField
                label="Background Image"
                value={getContent('wedding', 'hero', 'image')}
                onChange={(e) => updateField('wedding_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('wedding_hero', 'image')}
                altValue={getContent('wedding', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('wedding_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('wedding', 'hero', 'hero', content['wedding_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
            </div>
          </ContentSection>

          {/* Wedding Venues */}
          {[1, 2, 3].map((num) => (
            <ContentSection key={`venue${num}`} title={`Wedding Venue ${num}`}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nama Venue</Label>
                    <Input value={getContent('wedding', `venue${num}`, 'name')} onChange={(e) => updateField(`wedding_venue${num}`, 'name', e.target.value)} placeholder="Grand Ballroom" />
                  </div>
                  <div>
                    <Label>Kapasitas</Label>
                    <Input value={getContent('wedding', `venue${num}`, 'capacity')} onChange={(e) => updateField(`wedding_venue${num}`, 'capacity', e.target.value)} placeholder="300 - 500 guests" />
                  </div>
                </div>
                <div>
                  <Label>Deskripsi</Label>
                  <Textarea value={getContent('wedding', `venue${num}`, 'description')} onChange={(e) => updateField(`wedding_venue${num}`, 'description', e.target.value)} rows={2} placeholder="Our magnificent ballroom..." />
                </div>
                <ImageField
                  label="Gambar"
                  value={getContent('wedding', `venue${num}`, 'image')}
                  onChange={(e) => updateField(`wedding_venue${num}`, 'image', e.target.value)}
                  onUpload={() => openMediaUpload(`wedding_venue${num}`, 'image')}
                  altValue={getContent('wedding', `venue${num}`, 'imageAlt')}
                  onAltChange={(e) => updateField(`wedding_venue${num}`, 'imageAlt', e.target.value)}
                />
                <Button onClick={() => saveContent('wedding', `venue${num}`, 'venue', content[`wedding_venue${num}`]?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Save className="w-4 h-4 mr-2" />Simpan Venue {num}
                </Button>
              </div>
            </ContentSection>
          ))}

          {/* Wedding Services */}
          {[
            { num: 1, default: 'Floral Design' },
            { num: 2, default: 'Catering' },
            { num: 3, default: 'Photography' },
            { num: 4, default: 'Entertainment' }
          ].map((svc) => (
            <ContentSection key={`service${svc.num}`} title={`Wedding Service ${svc.num}`}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nama Service</Label>
                    <Input value={getContent('wedding', `service${svc.num}`, 'name')} onChange={(e) => updateField(`wedding_service${svc.num}`, 'name', e.target.value)} placeholder={svc.default} />
                  </div>
                  <div>
                    <Label>Deskripsi Singkat</Label>
                    <Input value={getContent('wedding', `service${svc.num}`, 'description')} onChange={(e) => updateField(`wedding_service${svc.num}`, 'description', e.target.value)} placeholder="Custom floral arrangements" />
                  </div>
                </div>
                <Button onClick={() => saveContent('wedding', `service${svc.num}`, 'service', content[`wedding_service${svc.num}`]?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Save className="w-4 h-4 mr-2" />Simpan Service {svc.num}
                </Button>
              </div>
            </ContentSection>
          ))}

          {/* Wedding CTA */}
          <ContentSection title="CTA Section (Bawah)">
            <div className="space-y-4">
              <div>
                <Label>Judul CTA</Label>
                <Input value={getContent('wedding', 'cta', 'title')} onChange={(e) => updateField('wedding_cta', 'title', e.target.value)} placeholder="Begin Your Forever" />
              </div>
              <div>
                <Label>Deskripsi CTA</Label>
                <Textarea value={getContent('wedding', 'cta', 'description')} onChange={(e) => updateField('wedding_cta', 'description', e.target.value)} rows={2} placeholder="Let us help you plan the wedding of your dreams" />
              </div>
              <Button onClick={() => saveContent('wedding', 'cta', 'cta', content['wedding_cta']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan CTA
              </Button>
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== FACILITIES PAGE (DYNAMIC) ==================== */}
        <TabsContent value="facilities" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Facilities</span>
            <div className="flex gap-2">
              <Button onClick={() => saveAllForPage('facilities')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
              <Button onClick={() => { setEditingFacility({}); setShowFacilityModal(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Plus className="w-4 h-4 mr-2" />Tambah Fasilitas
              </Button>
            </div>
          </div>

          <ContentSection title="Facilities Page Hero">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('facilities', 'hero', 'title')} onChange={(e) => updateField('facilities_hero', 'title', e.target.value)} placeholder="Our Facilities" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('facilities', 'hero', 'subtitle')} onChange={(e) => updateField('facilities_hero', 'subtitle', e.target.value)} placeholder="Premium Amenities" />
              </div>
              <ImageField
                label="Background Image"
                value={getContent('facilities', 'hero', 'image')}
                onChange={(e) => updateField('facilities_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('facilities_hero', 'image')}
                altValue={getContent('facilities', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('facilities_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('facilities', 'hero', 'hero', content['facilities_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
            </div>
          </ContentSection>

          {/* Dynamic Facilities List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {facilities.length === 0 ? (
              <div className="col-span-full text-center py-8 text-gray-500 bg-white rounded-xl shadow-soft">
                Belum ada fasilitas. Klik "Tambah Fasilitas" untuk memulai.
              </div>
            ) : (
              facilities.map((facility) => (
                <div key={facility.id} className={`bg-white rounded-xl shadow-soft overflow-hidden border ${!facility.is_active ? 'opacity-60 border-gray-200' : 'border-emerald-100'}`}>
                  <div className="relative h-40 bg-gray-100">
                    {facility.image ? (
                      <img src={facility.image} alt={facility.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">No Image</div>
                    )}
                    {!facility.is_active && (
                      <div className="absolute inset-0 bg-gray-900/10 flex items-center justify-center">
                        <span className="bg-gray-800 text-white text-xs px-2 py-1 rounded">Non-aktif</span>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                      Urutan: {facility.order}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-1">{facility.name}</h4>
                    {facility.hours && <p className="text-xs text-emerald-600 font-medium mb-2">{facility.hours}</p>}
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4">{facility.description}</p>

                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => { setEditingFacility(facility); setShowFacilityModal(true); }}>
                        Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteFacility(facility.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <ContentSection title="Experience Complete Comfort (Info Bawah)">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input
                  value={getContent('facilities', 'info', 'title')}
                  onChange={(e) => updateField('facilities_info', 'title', e.target.value)}
                  placeholder="Experience Complete Comfort"
                />
              </div>
              <div>
                <Label>Deskripsi</Label>
                <Textarea
                  value={getContent('facilities', 'info', 'description')}
                  onChange={(e) => updateField('facilities_info', 'description', e.target.value)}
                  rows={3}
                  placeholder="All facilities are available exclusively..."
                />
              </div>
              <Button onClick={() => saveContent('facilities', 'info', 'section', content['facilities_info']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Info
              </Button>
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== GALLERY PAGE ==================== */}
        <TabsContent value="gallery" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Gallery</span>
            <Button onClick={() => saveAllForPage('gallery')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>
          <ContentSection title="Gallery Page Hero">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('gallery', 'hero', 'title')} onChange={(e) => updateField('gallery_hero', 'title', e.target.value)} placeholder="Gallery" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('gallery', 'hero', 'subtitle')} onChange={(e) => updateField('gallery_hero', 'subtitle', e.target.value)} placeholder="Explore Our Hotel" />
              </div>
              <ImageField
                label="Background Image"
                value={getContent('gallery', 'hero', 'image')}
                onChange={(e) => updateField('gallery_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('gallery_hero', 'image')}
                altValue={getContent('gallery', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('gallery_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('gallery', 'hero', 'hero', content['gallery_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
            </div>
          </ContentSection>

          <ContentSection title="Gallery Images">
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Upload unlimited photos. Use Order to sort them.
                </p>
                <Button onClick={addGalleryItem} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Image
                </Button>
              </div>
              {getGalleryItems().map((key) => {
                const sectionName = key.replace('gallery_', '');
                return (
                  <div key={key} className="flex gap-4 items-center p-3 bg-gray-50 rounded-lg">
                    {getContent('gallery', sectionName, 'url') && (
                      <img src={getContent('gallery', sectionName, 'url')} alt="Gallery" className="w-20 h-14 object-cover rounded" />
                    )}
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={getContent('gallery', sectionName, 'url')}
                          onChange={(e) => updateField(key, 'url', e.target.value)}
                          placeholder="Image URL"
                          className="flex-1"
                        />
                        <Button variant="outline" size="sm" onClick={() => openMediaUpload(key, 'url')}>
                          <Upload className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={getContent('gallery', sectionName, 'alt')}
                          onChange={(e) => updateField(key, 'alt', e.target.value)}
                          placeholder="Alt Text (deskripsi gambar)"
                          className="flex-[2]"
                        />
                        <Input
                          value={getContent('gallery', sectionName, 'caption')}
                          onChange={(e) => updateField(key, 'caption', e.target.value)}
                          placeholder="Caption"
                          className="flex-[2]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          value={getContent('gallery', sectionName, 'order', 0)}
                          onChange={(e) => updateField(key, 'order', parseInt(e.target.value))}
                          placeholder="Order"
                          className="w-20"
                        />
                        <select
                          value={getContent('gallery', sectionName, 'category', 'general')}
                          onChange={(e) => updateField(key, 'category', e.target.value)}
                          className="border rounded px-2 text-sm flex-1"
                        >
                          <option value="general">General</option>
                          <option value="rooms">Rooms</option>
                          <option value="facilities">Facilities</option>
                          <option value="restaurant">Restaurant</option>
                          <option value="events">Events</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        onClick={() => saveContent('gallery', sectionName, 'image', content[key]?.content)}
                        disabled={isSaving}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => deleteContent('gallery', sectionName)}
                        disabled={isSaving}
                        size="sm"
                        variant="destructive"
                        className="bg-red-500 hover:bg-red-600 text-white"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
              {getGalleryItems().length === 0 && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  No images yet. Click "Add Image" to start.
                </div>
              )}
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== SALES GALLERY PAGE ==================== */}
        <TabsContent value="sales_gallery" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Sales Gallery</span>
            <div className="flex gap-2">
              <Button onClick={() => openMediaUpload('sales_gallery_bulk', null, 'auto')} variant="outline" className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-emerald-300">
                <Upload className="w-4 h-4 mr-2" />Bulk Upload Media
              </Button>
              <Button onClick={addSalesItem} variant="outline" className="bg-white">
                <Plus className="w-4 h-4 mr-2" />Tambah Item
              </Button>
              <Button onClick={() => saveAllForPage('sales_gallery')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Semua
              </Button>
            </div>
          </div>
          
          <ContentSection title="Daftar Media Sales Gallery (Drag & Drop untuk Mengurutkan)">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Tampilkan:</span>
                    <select
                      value={salesItemsPerPage}
                      onChange={(e) => {
                        setSalesItemsPerPage(Number(e.target.value));
                        setSalesCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-600">item</span>
                  </div>

                  <div className="flex items-center gap-2 border-l pl-4">
                    <span className="text-sm text-gray-600">Kategori:</span>
                    <select
                      value={salesCategoryFilter}
                      onChange={(e) => {
                        setSalesCategoryFilter(e.target.value);
                        setSalesCurrentPage(1);
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                    >
                      <option value="Semua">Semua Kategori</option>
                      {displayCats.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {(() => {
                  const totalItems = Object.keys(content)
                    .filter(matchesSalesFilter).length;
                  const totalPages = Math.ceil(totalItems / salesItemsPerPage);
                  return totalPages > 1 ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSalesCurrentPage(p => Math.max(1, p - 1))}
                        disabled={salesCurrentPage === 1}
                      >
                        Prev
                      </Button>
                      <span className="text-sm text-gray-600">
                        Halaman {salesCurrentPage} dari {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSalesCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={salesCurrentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  ) : null;
                })()}
              </div>

              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (active.id !== over.id) {
                    const sortedKeys = Object.keys(content)
                      .filter(key => key.startsWith('sales_gallery_item_'))
                      .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0));
                    
                    const oldIndex = sortedKeys.indexOf(active.id);
                    const newIndex = sortedKeys.indexOf(over.id);
                    
                    const newSortedKeys = arrayMove(sortedKeys, oldIndex, newIndex);
                    
                    // Update orders locally
                    setContent(prev => {
                      const next = { ...prev };
                      newSortedKeys.forEach((key, index) => {
                        if (next[key] && next[key].content) {
                           next[key].content.order = index;
                        }
                      });
                      return next;
                    });
                  }
                }}
              >
                <SortableContext 
                  items={Object.keys(content)
                    .filter(matchesSalesFilter)
                    .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0))
                    .slice((salesCurrentPage - 1) * salesItemsPerPage, salesCurrentPage * salesItemsPerPage)}
                  strategy={rectSortingStrategy}
                >
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 px-2">
                    {Object.keys(content)
                      .filter(matchesSalesFilter)
                      .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0))
                      .slice((salesCurrentPage - 1) * salesItemsPerPage, salesCurrentPage * salesItemsPerPage)
                      .map((key) => {
                        const sectionName = key.replace('sales_gallery_', '');
                        return (
                          <SortableGalleryItem 
                            key={key} 
                            id={key} 
                            sectionName={sectionName} 
                            contentKey={key}
                            content={content}
                            getContent={getContent}
                            updateField={updateField}
                            openMediaUpload={openMediaUpload}
                            saveContent={saveContent}
                            deleteContent={deleteContent}
                            isSaving={isSaving}
                            setEditingKey={setEditingSalesKey}
                            categories={displayCats}
                          />
                        );
                      })}
                  </div>
                </SortableContext>
              </DndContext>
              {Object.keys(content).filter(key => key.startsWith('sales_gallery_item_') && (salesCategoryFilter === 'Semua' || content[key]?.content?.category === salesCategoryFilter)).length === 0 && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  Belum ada foto/video untuk kategori ini.
                </div>
              )}
            </div>
          </ContentSection>

          <ContentSection title="Urutan Kategori Sales Gallery">
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Geser (drag & drop) untuk mengatur urutan kategori yang tampil di website. Rename (pensil) untuk mengganti nama kategori, hapus (tempah) untuk menghapusnya — kategori yang masih berisi item tidak bisa dihapus.
              </p>
              <div className="flex gap-2 max-w-sm mb-3">
                <Input
                  value={newSalesCategory}
                  onChange={(e) => setNewSalesCategory(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addSalesCategory(); }}
                  placeholder="Nama kategori baru"
                />
                <Button
                  type="button"
                  onClick={addSalesCategory}
                  disabled={isSaving || !newSalesCategory.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                >
                  <Plus className="w-4 h-4 mr-1" />Tambah Kategori
                </Button>
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (active.id !== over.id) {
                    const oldIndex = displayCats.indexOf(active.id);
                    const newIndex = displayCats.indexOf(over.id);
                    const newArray = arrayMove(displayCats, oldIndex, newIndex);

                    // Save directly
                    saveContent('sales_gallery', 'category_order', 'config', { order: newArray });
                    // Optimistically update local state so no flash
                    updateField('sales_gallery_category_order', 'order', newArray);
                    if (!content['sales_gallery_category_order']) {
                        setContent(prev => ({
                            ...prev,
                            'sales_gallery_category_order': {
                                page: 'sales_gallery',
                                section: 'category_order',
                                content_type: 'config',
                                content: { order: newArray }
                            }
                        }));
                    }
                  }
                }}
              >
                <SortableContext items={displayCats} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-col gap-2 max-w-sm">
                    {displayCats.map((cat, idx) => (
                      <CategorySortableItem
                        key={cat}
                        id={cat}
                        index={idx}
                        count={categoryCounts[cat] || 0}
                        onRename={(newName) => renameSalesCategory(cat, newName)}
                        onDelete={() => deleteSalesCategory(cat)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== RESTORAN PAGE ==================== */}
        <TabsContent value="restoran" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Kenari Restaurant</span>
            <Button onClick={() => saveAllForPage('restaurant')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>

          {/* Hero Slide 1 */}
          <ContentSection title="Hero Slide 1 (Utama)">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('restaurant', 'hero', 'title')} onChange={(e) => updateField('restaurant_hero', 'title', e.target.value)} placeholder="Kenari Restaurant" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('restaurant', 'hero', 'subtitle')} onChange={(e) => updateField('restaurant_hero', 'subtitle', e.target.value)} placeholder="Culinary Experience" />
              </div>
              <div>
                <Label>Deskripsi (opsional)</Label>
                <Textarea value={getContent('restaurant', 'hero', 'description')} onChange={(e) => updateField('restaurant_hero', 'description', e.target.value)} rows={2} placeholder="Nikmati sajian terbaik..." />
              </div>
              <ImageField
                label="Gambar Hero Slide 1"
                value={getContent('restaurant', 'hero', 'image')}
                onChange={(e) => updateField('restaurant_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('restaurant_hero', 'image')}
                altValue={getContent('restaurant', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('restaurant_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('restaurant', 'hero', 'hero', content['restaurant_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Slide 1
              </Button>
            </div>
          </ContentSection>

          {/* Hero Slide 2 */}
          <ContentSection title="Hero Slide 2">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('restaurant', 'hero_slide_2', 'title')} onChange={(e) => updateField('restaurant_hero_slide_2', 'title', e.target.value)} placeholder="Authentic Indonesian Cuisine" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('restaurant', 'hero_slide_2', 'subtitle')} onChange={(e) => updateField('restaurant_hero_slide_2', 'subtitle', e.target.value)} placeholder="Fresh & Local Ingredients" />
              </div>
              <ImageField
                label="Gambar Hero Slide 2"
                value={getContent('restaurant', 'hero_slide_2', 'image')}
                onChange={(e) => updateField('restaurant_hero_slide_2', 'image', e.target.value)}
                onUpload={() => openMediaUpload('restaurant_hero_slide_2', 'image')}
                altValue={getContent('restaurant', 'hero_slide_2', 'imageAlt')}
                onAltChange={(e) => updateField('restaurant_hero_slide_2', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('restaurant', 'hero_slide_2', 'hero', content['restaurant_hero_slide_2']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Slide 2
              </Button>
            </div>
          </ContentSection>

          {/* Hero Slide 3 */}
          <ContentSection title="Hero Slide 3">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('restaurant', 'hero_slide_3', 'title')} onChange={(e) => updateField('restaurant_hero_slide_3', 'title', e.target.value)} placeholder="Mountain View Dining" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('restaurant', 'hero_slide_3', 'subtitle')} onChange={(e) => updateField('restaurant_hero_slide_3', 'subtitle', e.target.value)} placeholder="A Taste of Nature" />
              </div>
              <ImageField
                label="Gambar Hero Slide 3"
                value={getContent('restaurant', 'hero_slide_3', 'image')}
                onChange={(e) => updateField('restaurant_hero_slide_3', 'image', e.target.value)}
                onUpload={() => openMediaUpload('restaurant_hero_slide_3', 'image')}
                altValue={getContent('restaurant', 'hero_slide_3', 'imageAlt')}
                onAltChange={(e) => updateField('restaurant_hero_slide_3', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('restaurant', 'hero_slide_3', 'hero', content['restaurant_hero_slide_3']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Slide 3
              </Button>
            </div>
          </ContentSection>

          {/* Menu Images (Dynamic) */}
          <ContentSection title="Foto Menu Kenari Restaurant (A4)">
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 text-sm">
                  <strong>Info:</strong> Upload foto menu berukuran A4 (portrait). Foto akan ditampilkan dengan animasi page-flip di halaman Kenari Restaurant. Gunakan urutan (Order) untuk mengatur posisi halaman.
                </p>
              </div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Upload foto menu. Gunakan Order untuk mengurutkan.
                </p>
                <Button onClick={() => {
                  const timestamp = new Date().getTime();
                  const section = `menu_item_${timestamp}`;
                  const key = `restaurant_${section}`;
                  updateField(key, 'url', '');
                  updateField(key, 'caption', '');
                  updateField(key, 'order', Object.keys(content).filter(k => k.startsWith('restaurant_menu_item_')).length);
                }} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Halaman Menu
                </Button>
              </div>
              {Object.keys(content)
                .filter(key => key.startsWith('restaurant_menu_item_'))
                .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0))
                .map((key) => {
                  const sectionName = key.replace('restaurant_', '');
                  return (
                    <div key={key} className="flex gap-4 items-center p-3 bg-gray-50 rounded-lg">
                      {getContent('restaurant', sectionName, 'url') && (
                        <img src={getContent('restaurant', sectionName, 'url')} alt="Menu" className="w-16 h-22 object-cover rounded border" />
                      )}
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={getContent('restaurant', sectionName, 'url')}
                            onChange={(e) => updateField(key, 'url', e.target.value)}
                            placeholder="Image URL"
                            className="flex-1"
                          />
                          <Button variant="outline" size="sm" onClick={() => openMediaUpload(key, 'url')}>
                            <Upload className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={getContent('restaurant', sectionName, 'caption')}
                            onChange={(e) => updateField(key, 'caption', e.target.value)}
                            placeholder="Caption (opsional)"
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            value={getContent('restaurant', sectionName, 'order', 0)}
                            onChange={(e) => updateField(key, 'order', parseInt(e.target.value))}
                            placeholder="Order"
                            className="w-20"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          onClick={() => saveContent('restaurant', sectionName, 'image', content[key]?.content)}
                          disabled={isSaving}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => deleteContent('restaurant', sectionName)}
                          disabled={isSaving}
                          size="sm"
                          variant="destructive"
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              {Object.keys(content).filter(k => k.startsWith('restaurant_menu_item_')).length === 0 && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  Belum ada foto menu. Klik "Tambah Halaman Menu" untuk memulai.
                </div>
              )}
            </div>
          </ContentSection>

          {/* CTA Section */}
          <ContentSection title="CTA Section (Bawah)">
            <div className="space-y-4">
              <div>
                <Label>Judul CTA</Label>
                <Input value={getContent('restaurant', 'cta', 'title')} onChange={(e) => updateField('restaurant_cta', 'title', e.target.value)} placeholder="Reservasi Meja Kenari Restaurant" />
              </div>
              <div>
                <Label>Deskripsi CTA</Label>
                <Textarea value={getContent('restaurant', 'cta', 'description')} onChange={(e) => updateField('restaurant_cta', 'description', e.target.value)} rows={2} placeholder="Nikmati pengalaman kuliner terbaik..." />
              </div>
              <Button onClick={() => saveContent('restaurant', 'cta', 'cta', content['restaurant_cta']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan CTA
              </Button>
            </div>
          </ContentSection>
        </TabsContent>

{/* ==================== POOL CAFE PAGE ==================== */}
        <TabsContent value="pool_cafe" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Rintik Rindu Pool Cafe</span>
            <Button onClick={() => saveAllForPage('pool_cafe')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>

          {/* Hero Slide 1 */}
          <ContentSection title="Hero Slide 1 (Utama)">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('pool_cafe', 'hero', 'title')} onChange={(e) => updateField('pool_cafe_hero', 'title', e.target.value)} placeholder="Rintik Rindu Pool Cafe" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('pool_cafe', 'hero', 'subtitle')} onChange={(e) => updateField('pool_cafe_hero', 'subtitle', e.target.value)} placeholder="Culinary Experience" />
              </div>
              <div>
                <Label>Deskripsi (opsional)</Label>
                <Textarea value={getContent('pool_cafe', 'hero', 'description')} onChange={(e) => updateField('pool_cafe_hero', 'description', e.target.value)} rows={2} placeholder="Nikmati sajian terbaik..." />
              </div>
              <ImageField
                label="Gambar Hero Slide 1"
                value={getContent('pool_cafe', 'hero', 'image')}
                onChange={(e) => updateField('pool_cafe_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('pool_cafe_hero', 'image')}
                altValue={getContent('pool_cafe', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('pool_cafe_hero', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('pool_cafe', 'hero', 'hero', content['pool_cafe_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Slide 1
              </Button>
            </div>
          </ContentSection>

          {/* Hero Slide 2 */}
          <ContentSection title="Hero Slide 2">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('pool_cafe', 'hero_slide_2', 'title')} onChange={(e) => updateField('pool_cafe_hero_slide_2', 'title', e.target.value)} placeholder="Authentic Indonesian Cuisine" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('pool_cafe', 'hero_slide_2', 'subtitle')} onChange={(e) => updateField('pool_cafe_hero_slide_2', 'subtitle', e.target.value)} placeholder="Fresh & Local Ingredients" />
              </div>
              <ImageField
                label="Gambar Hero Slide 2"
                value={getContent('pool_cafe', 'hero_slide_2', 'image')}
                onChange={(e) => updateField('pool_cafe_hero_slide_2', 'image', e.target.value)}
                onUpload={() => openMediaUpload('pool_cafe_hero_slide_2', 'image')}
                altValue={getContent('pool_cafe', 'hero_slide_2', 'imageAlt')}
                onAltChange={(e) => updateField('pool_cafe_hero_slide_2', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('pool_cafe', 'hero_slide_2', 'hero', content['pool_cafe_hero_slide_2']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Slide 2
              </Button>
            </div>
          </ContentSection>

          {/* Hero Slide 3 */}
          <ContentSection title="Hero Slide 3">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('pool_cafe', 'hero_slide_3', 'title')} onChange={(e) => updateField('pool_cafe_hero_slide_3', 'title', e.target.value)} placeholder="Mountain View Dining" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('pool_cafe', 'hero_slide_3', 'subtitle')} onChange={(e) => updateField('pool_cafe_hero_slide_3', 'subtitle', e.target.value)} placeholder="A Taste of Nature" />
              </div>
              <ImageField
                label="Gambar Hero Slide 3"
                value={getContent('pool_cafe', 'hero_slide_3', 'image')}
                onChange={(e) => updateField('pool_cafe_hero_slide_3', 'image', e.target.value)}
                onUpload={() => openMediaUpload('pool_cafe_hero_slide_3', 'image')}
                altValue={getContent('pool_cafe', 'hero_slide_3', 'imageAlt')}
                onAltChange={(e) => updateField('pool_cafe_hero_slide_3', 'imageAlt', e.target.value)}
              />
              <Button onClick={() => saveContent('pool_cafe', 'hero_slide_3', 'hero', content['pool_cafe_hero_slide_3']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Slide 3
              </Button>
            </div>
          </ContentSection>

          {/* Menu Images (Dynamic) */}
          <ContentSection title="Foto Menu Rintik Rindu Pool Cafe (A4)">
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 text-sm">
                  <strong>Info:</strong> Upload foto menu berukuran A4 (portrait). Foto akan ditampilkan dengan animasi page-flip di halaman Rintik Rindu Pool Cafe. Gunakan urutan (Order) untuk mengatur posisi halaman.
                </p>
              </div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  Upload foto menu. Gunakan Order untuk mengurutkan.
                </p>
                <Button onClick={() => {
                  const timestamp = new Date().getTime();
                  const section = `menu_item_${timestamp}`;
                  const key = `pool_cafe_${section}`;
                  updateField(key, 'url', '');
                  updateField(key, 'caption', '');
                  updateField(key, 'order', Object.keys(content).filter(k => k.startsWith('pool_cafe_menu_item_')).length);
                }} size="sm" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Halaman Menu
                </Button>
              </div>
              {Object.keys(content)
                .filter(key => key.startsWith('pool_cafe_menu_item_'))
                .sort((a, b) => (content[a]?.content?.order || 0) - (content[b]?.content?.order || 0))
                .map((key) => {
                  const sectionName = key.replace('pool_cafe_', '');
                  return (
                    <div key={key} className="flex gap-4 items-center p-3 bg-gray-50 rounded-lg">
                      {getContent('pool_cafe', sectionName, 'url') && (
                        <img src={getContent('pool_cafe', sectionName, 'url')} alt="Menu" className="w-16 h-22 object-cover rounded border" />
                      )}
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={getContent('pool_cafe', sectionName, 'url')}
                            onChange={(e) => updateField(key, 'url', e.target.value)}
                            placeholder="Image URL"
                            className="flex-1"
                          />
                          <Button variant="outline" size="sm" onClick={() => openMediaUpload(key, 'url')}>
                            <Upload className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={getContent('pool_cafe', sectionName, 'caption')}
                            onChange={(e) => updateField(key, 'caption', e.target.value)}
                            placeholder="Caption (opsional)"
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            value={getContent('pool_cafe', sectionName, 'order', 0)}
                            onChange={(e) => updateField(key, 'order', parseInt(e.target.value))}
                            placeholder="Order"
                            className="w-20"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          onClick={() => saveContent('pool_cafe', sectionName, 'image', content[key]?.content)}
                          disabled={isSaving}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => deleteContent('pool_cafe', sectionName)}
                          disabled={isSaving}
                          size="sm"
                          variant="destructive"
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              {Object.keys(content).filter(k => k.startsWith('pool_cafe_menu_item_')).length === 0 && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  Belum ada foto menu. Klik "Tambah Halaman Menu" untuk memulai.
                </div>
              )}
            </div>
          </ContentSection>

          {/* CTA Section */}
          <ContentSection title="CTA Section (Bawah)">
            <div className="space-y-4">
              <div>
                <Label>Judul CTA</Label>
                <Input value={getContent('pool_cafe', 'cta', 'title')} onChange={(e) => updateField('pool_cafe_cta', 'title', e.target.value)} placeholder="Reservasi Meja Rintik Rindu Pool Cafe" />
              </div>
              <div>
                <Label>Deskripsi CTA</Label>
                <Textarea value={getContent('pool_cafe', 'cta', 'description')} onChange={(e) => updateField('pool_cafe_cta', 'description', e.target.value)} rows={2} placeholder="Nikmati pengalaman kuliner terbaik..." />
              </div>
              <Button onClick={() => saveContent('pool_cafe', 'cta', 'cta', content['pool_cafe_cta']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan CTA
              </Button>
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== FOOTER ==================== */}
        <TabsContent value="footer" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten Footer (Global)</span>
            <Button onClick={() => saveAllForPage('global')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>
          <ContentSection title="Informasi Kontak">
            <div className="space-y-4">
              <div>
                <Label>Deskripsi Hotel</Label>
                <Textarea
                  value={getContent('global', 'footer', 'description')}
                  onChange={(e) => updateField('global_footer', 'description', e.target.value)}
                  placeholder="Experience luxury and comfort in the heart of Batu city. Your perfect getaway destination."
                  rows={2}
                />
              </div>
              <div>
                <Label>Alamat</Label>
                <Textarea
                  value={getContent('global', 'footer', 'address')}
                  onChange={(e) => updateField('global_footer', 'address', e.target.value)}
                  placeholder="Jl. Raya Punten No.86, Kec. Bumiaji, Kota Batu, Jawa Timur 65338 Indonesia"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Telepon</Label>
                  <Input value={getContent('global', 'footer', 'phone')} onChange={(e) => updateField('global_footer', 'phone', e.target.value)} placeholder="+6281130700206" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={getContent('global', 'footer', 'email')} onChange={(e) => updateField('global_footer', 'email', e.target.value)} placeholder="reservasi@spencergreenhotel.com" />
                </div>
              </div>
            </div>
          </ContentSection>

          <ContentSection title="Social Media Links">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>TikTok URL</Label>
                  <Input value={getContent('global', 'footer', 'tiktok')} onChange={(e) => updateField('global_footer', 'tiktok', e.target.value)} placeholder="https://tiktok.com/@..." />
                </div>
                <div>
                  <Label>Instagram URL</Label>
                  <Input value={getContent('global', 'footer', 'instagram')} onChange={(e) => updateField('global_footer', 'instagram', e.target.value)} placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <Label>Facebook URL</Label>
                  <Input value={getContent('global', 'footer', 'facebook')} onChange={(e) => updateField('global_footer', 'facebook', e.target.value)} placeholder="https://facebook.com/..." />
                </div>
                <div>
                  <Label>WhatsApp Number</Label>
                  <Input value={getContent('global', 'footer', 'whatsapp')} onChange={(e) => updateField('global_footer', 'whatsapp', e.target.value)} placeholder="6281130700206" />
                </div>
              </div>
              <Button onClick={() => saveContent('global', 'footer', 'info', content['global_footer']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Footer
              </Button>
            </div>
          </ContentSection>

          <ContentSection title="Google Maps">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Latitude</Label>
                  <Input value={getContent('global', 'map', 'lat')} onChange={(e) => updateField('global_map', 'lat', e.target.value)} placeholder="-7.8332533" />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input value={getContent('global', 'map', 'lng')} onChange={(e) => updateField('global_map', 'lng', e.target.value)} placeholder="112.5288334" />
                </div>
              </div>
              <Button onClick={() => saveContent('global', 'map', 'location', content['global_map']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Lokasi
              </Button>
            </div>
          </ContentSection>
        </TabsContent>

        {/* ==================== SPECIAL OFFERS ==================== */}
        <TabsContent value="offers" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Kelola Special Offers di halaman Home</span>
            <Button onClick={() => { setEditingOffer({}); setShowOfferModal(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-2" />Tambah Offer
            </Button>
          </div>

          {offers.length === 0 ? (
            <div className="bg-white rounded-xl p-8 shadow-soft text-center text-gray-500">
              Belum ada special offers. Klik "Tambah Offer" untuk membuat.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...offers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((offer, index, sortedArr) => (
                <div key={offer.id} className="bg-white rounded-xl shadow-soft overflow-hidden">
                  {offer.image && (
                    <img src={offer.image} alt={offer.title} className="w-full h-32 object-cover" />
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-gray-900">{offer.title}</h4>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => reorderOffer(offer.id, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded hover:bg-gray-100 transition-colors ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}
                          title="Pindah ke atas"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => reorderOffer(offer.id, 'down')}
                          disabled={index === sortedArr.length - 1}
                          className={`p-1 rounded hover:bg-gray-100 transition-colors ${index === sortedArr.length - 1 ? 'opacity-30 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`}
                          title="Pindah ke bawah"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-gray-400 ml-1">#{index + 1}</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{offer.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">{offer.code}</span>
                      <span className="text-xs text-gray-500">s/d {offer.validUntil}</span>
                      {offer.promo_media_url && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">🖼 Promo Media</span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => { setEditingOffer(offer); setShowOfferModal(true); }}>
                        Edit
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteOffer(offer.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ==================== TABLE MANNER PAGE ==================== */}
        <TabsContent value="table-manner" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Edit konten halaman Table Manner</span>
            <Button onClick={() => saveAllForPage('table-manner')} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Semua Perubahan
            </Button>
          </div>

          {/* Hero Section */}
          <ContentSection title="Table Manner Hero">
            <div className="space-y-4">
              <div>
                <Label>Judul</Label>
                <Input value={getContent('table-manner', 'hero', 'title')} onChange={(e) => updateField('table-manner_hero', 'title', e.target.value)} placeholder="Paket Table Manner" />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={getContent('table-manner', 'hero', 'subtitle')} onChange={(e) => updateField('table-manner_hero', 'subtitle', e.target.value)} placeholder="Etiquette Training" />
              </div>
              <div>
                <Label>Deskripsi Hero</Label>
                <Textarea value={getContent('table-manner', 'hero', 'description')} onChange={(e) => updateField('table-manner_hero', 'description', e.target.value)} placeholder="Pelajari etika makan internasional..." rows={2} />
              </div>
              <ImageField
                label="Gambar Hero"
                value={getContent('table-manner', 'hero', 'image')}
                onChange={(e) => updateField('table-manner_hero', 'image', e.target.value)}
                onUpload={() => openMediaUpload('table-manner_hero', 'image')}
                altValue={getContent('table-manner', 'hero', 'imageAlt')}
                onAltChange={(e) => updateField('table-manner_hero', 'imageAlt', e.target.value)}
              />
              <div>
                <Label>Video URL Hero (opsional)</Label>
                <Input
                  value={getContent('table-manner', 'hero', 'video')}
                  onChange={(e) => updateField('table-manner_hero', 'video', e.target.value)}
                  placeholder="https://youtube.com/watch?v=... atau link .mp4"
                />
                <p className="text-xs text-gray-500 mt-1">Mendukung YouTube dan link video langsung. Kosongkan untuk tampilkan gambar saja.</p>
                {getContent('table-manner', 'hero', 'video') && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-emerald-600 font-medium">✓ Video aktif</span>
                    <Button variant="outline" size="sm" onClick={() => updateField('table-manner_hero', 'video', '')}>
                      <Trash2 className="w-3 h-3 mr-1" />Hapus Video
                    </Button>
                  </div>
                )}
              </div>
              <Button onClick={() => saveContent('table-manner', 'hero', 'hero', content['table-manner_hero']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Hero
              </Button>
            </div>
          </ContentSection>

          {/* Etiquette Video */}
          <ContentSection title="Video Standar Etiket Makan">
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 text-sm">
                  <strong>Info:</strong> Video ini ditampilkan pada tombol "Standar Etiket Makan yang Kami Ajarkan di Spencer Green" di halaman Table Manner. Pengunjung bisa klik tombol tersebut untuk melihat video ini.
                </p>
              </div>
              <div>
                <Label>URL Video Etiket Makan</Label>
                <div className="flex gap-2">
                  <Input
                    value={getContent('table-manner', 'etiquette_video', 'video_url')}
                    onChange={(e) => updateField('table-manner_etiquette_video', 'video_url', e.target.value)}
                    placeholder="https://youtube.com/watch?v=... atau link .mp4"
                    className="flex-1"
                    data-testid="etiquette-video-url-input"
                  />
                  <Button type="button" variant="outline" onClick={() => openMediaUpload('table-manner_etiquette_video', 'video_url', 'video')}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Video
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Mendukung link YouTube atau upload video langsung (.mp4, .webm). Max 500MB.</p>
                {getContent('table-manner', 'etiquette_video', 'video_url') && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-emerald-600 font-medium">✓ Video aktif</span>
                    <Button variant="outline" size="sm" onClick={() => updateField('table-manner_etiquette_video', 'video_url', '')}>
                      <Trash2 className="w-3 h-3 mr-1" />Hapus Video
                    </Button>
                  </div>
                )}
              </div>
              <Button onClick={() => saveContent('table-manner', 'etiquette_video', 'video', content['table-manner_etiquette_video']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />Simpan Video Etiket
              </Button>
            </div>
          </ContentSection>

          {/* Table Manner Packages */}
          {[1, 2, 3].map((num) => (
            <ContentSection key={`tm-pkg${num}`} title={`Paket Table Manner ${num}`}>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Nama Paket</Label>
                    <Input value={getContent('table-manner', `package${num}`, 'name')} onChange={(e) => updateField(`table-manner_package${num}`, 'name', e.target.value)} placeholder="Basic Table Manner" />
                  </div>
                  <div>
                    <Label>Kapasitas (pax)</Label>
                    <Input value={getContent('table-manner', `package${num}`, 'pax')} onChange={(e) => updateField(`table-manner_package${num}`, 'pax', e.target.value)} placeholder="20 - 30 pax" />
                  </div>
                  <div>
                    <Label>Harga</Label>
                    <Input value={getContent('table-manner', `package${num}`, 'price')} onChange={(e) => updateField(`table-manner_package${num}`, 'price', e.target.value)} placeholder="Rp 150.000/pax" />
                  </div>
                </div>
                <div>
                  <Label>Termasuk (pisahkan dengan baris baru)</Label>
                  <Textarea value={getContent('table-manner', `package${num}`, 'includes')} onChange={(e) => updateField(`table-manner_package${num}`, 'includes', e.target.value)} rows={3} placeholder={"Materi Etika Makan\nPraktek Langsung\nWelcome Drink\nSertifikat"} />
                </div>
                <ImageField
                  label="Gambar Paket"
                  value={getContent('table-manner', `package${num}`, 'image')}
                  onChange={(e) => updateField(`table-manner_package${num}`, 'image', e.target.value)}
                  onUpload={() => openMediaUpload(`table-manner_package${num}`, 'image')}
                  altValue={getContent('table-manner', `package${num}`, 'imageAlt')}
                  onAltChange={(e) => updateField(`table-manner_package${num}`, 'imageAlt', e.target.value)}
                />
                <Button onClick={() => saveContent('table-manner', `package${num}`, 'package', content[`table-manner_package${num}`]?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Save className="w-4 h-4 mr-2" />Simpan Paket {num}
                </Button>
              </div>
            </ContentSection>
          ))}
        </TabsContent>

        {/* ==================== SETTINGS PAGE ==================== */}
        <TabsContent value="settings" className="space-y-6">
          <div className="sticky top-0 z-10 bg-emerald-50/95 backdrop-blur-sm py-3 -mt-3 -mx-1 px-1 border-b border-emerald-100 flex justify-between items-center">
            <span className="text-sm text-gray-600">Pengaturan Sistem</span>
            <Button onClick={() => saveContent('settings', 'booking_engine', 'settings', content['settings_booking_engine']?.content || {})} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Save className="w-4 h-4 mr-2" />Simpan Pengaturan
            </Button>
          </div>
          <ContentSection title="Booking Engine & Payment Gateway">
            <div className="space-y-4">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 font-semibold text-gray-900">
                      {siteMinderStatus?.connected
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                      SiteMinder / Little Hotelier
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {siteMinderStatus?.connected
                        ? `${siteMinderStatus.property_name} · ${siteMinderStatus.mapped_rooms}/${siteMinderStatus.local_rooms} kamar terpetakan`
                        : siteMinderStatus?.message || 'Memeriksa koneksi...'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={siteMinderStatus?.connected ? syncSiteMinder : fetchSiteMinderStatus}
                    disabled={isSyncingSiteMinder}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isSyncingSiteMinder ? 'animate-spin' : ''}`} />
                    {siteMinderStatus?.connected ? 'Tes & Sinkronkan' : 'Periksa Ulang'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Direct Booking API menyediakan harga dan inventory real-time. Final checkout diarahkan ke halaman resmi SiteMinder agar reservasi langsung mengurangi inventory Channel Manager.
                </p>
                <div className="flex flex-wrap items-center justify-between pt-2 border-t border-emerald-200/60 gap-2">
                  <span className="text-xs text-emerald-900 font-medium">
                    Atur koneksi room type web dengan Tipe Kamar & Rate Plan SiteMinder:
                  </span>
                  <a
                    href="/admin/rooms?tab=siteminder-mapping"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-sm"
                  >
                    Atur Pemetaan Kamar SiteMinder →
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 py-2">
                <Switch
                  checked={getContent('settings', 'booking_engine', 'inventory_source', 'legacy') === 'siteminder'}
                  onCheckedChange={(enabled) => {
                    if (enabled && !siteMinderStatus?.connected) {
                      toast.error('Hubungkan SiteMinder sebelum mengaktifkan mode Channel Manager');
                      return;
                    }
                    updateField('settings_booking_engine', 'inventory_source', enabled ? 'siteminder' : 'legacy');
                  }}
                />
                <div>
                  <Label className="text-base">Gunakan Channel Manager SiteMinder</Label>
                  <p className="text-sm text-gray-500">
                    Mati: inventory & harga MongoDB lama. Aktif: quote real-time SiteMinder dan checkout SiteMinder.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 py-2 border-t border-emerald-100 pt-4">
                <Switch
                  checked={getContent(
                    'settings',
                    'booking_engine',
                    'checkout_provider',
                    'existing'
                  ) === 'siteminder'}
                  onCheckedChange={(enabled) => {
                    if (enabled && !siteMinderStatus?.connected) {
                      toast.error('Hubungkan SiteMinder sebelum memilih checkout SiteMinder');
                      return;
                    }
                    updateField('settings_booking_engine', 'checkout_provider', enabled ? 'siteminder' : 'existing');
                  }}
                />
                <div>
                  <Label className="text-base">Final Checkout melalui Booking Engine SiteMinder</Label>
                  <p className="text-sm text-gray-500">
                    Mati: checkout dan pembayaran existing (DOKU/WhatsApp). Aktif: redirect ke Booking Engine SiteMinder.
                  </p>
                </div>
              </div>

              {getContent('settings', 'booking_engine', 'inventory_source', 'legacy') === 'siteminder' &&
                getContent(
                  'settings',
                  'booking_engine',
                  'checkout_provider',
                  'existing'
                ) === 'existing' && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <p className="text-amber-900 text-sm font-medium">Perhatian: booking DOKU tidak otomatis masuk ke SiteMinder</p>
                  <p className="text-amber-800 text-xs mt-1">
                    Harga dan availability tetap divalidasi dari SiteMinder, tetapi reservasi yang berhasil harus segera dimasukkan manual ke Little Hotelier agar inventory Channel Manager berkurang.
                  </p>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  Aktifkan payment gateway (Doku) untuk memproses pembayaran secara online. Jika dinonaktifkan, booking akan berstatus "Pending" dan instruksi manual akan diberikan.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <Switch
                  checked={getContent('settings', 'booking_engine', 'enable_payment_gateway', false)}
                  onCheckedChange={(v) => updateField('settings_booking_engine', 'enable_payment_gateway', v)}
                />
                <div>
                  <Label className="text-base">Gunakan Payment Gateway (Doku)</Label>
                  <p className="text-sm text-gray-500">Jika mati, kembali ke sistem booking lama tanpa gateway.</p>
                </div>
              </div>
            </div>
          </ContentSection>
        </TabsContent>
      </Tabs>

      {/* Media Upload Dialog */}
      <Dialog open={showMediaUpload} onOpenChange={setShowMediaUpload}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Media</DialogTitle>
          </DialogHeader>
          <MediaUpload
            uploadEndpoint={`/media/upload/content-image?section=${uploadTarget?.key?.split('_')[0] || 'general'}`}
            acceptedTypes={
              uploadTarget?.mediaType === 'video'
                ? ['video/mp4', 'video/quicktime']
                : uploadTarget?.mediaType === 'auto'
                  ? ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm', 'video/quicktime']
                  : ['image/jpeg', 'image/png', 'image/webp']
            }
            maxFileSize={uploadTarget?.mediaType === 'image' ? 10 * 1024 * 1024 : 500 * 1024 * 1024}
            isMultiple={isBulkUpload}
            maxFiles={isBulkUpload ? 50 : 1}
            title={isBulkUpload ? 'Bulk Upload Media' : uploadTarget?.mediaType === 'video' ? 'Upload Video' : 'Upload Image'}
            description={
              isBulkUpload
                ? 'Pilih beberapa foto/video sekaligus (maksimal 50)'
                : uploadTarget?.mediaType === 'video'
                  ? 'Max 500MB, MP4/MOV'
                  : 'Max 10MB, JPEG/PNG/WebP'
            }
            cloudinaryResourceType={
              uploadTarget?.mediaType === 'video'
                ? 'video'
                : uploadTarget?.mediaType === 'auto'
                  ? 'auto'
                  : 'image'
            }
            onUploadSuccess={handleMediaUpload}
            onCloseDialog={() => setShowMediaUpload(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Special Offer Edit Modal */}
      <Dialog open={showOfferModal} onOpenChange={(v) => { setShowOfferModal(v); if (!v) setEditingOffer(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOffer?.id ? 'Edit Offer' : 'Tambah Offer'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const payload = {
              title: editingOffer?.title || '',
              description: editingOffer?.description || '',
              code: editingOffer?.code || '',
              image: editingOffer?.image || '',
              validUntil: editingOffer?.validUntil || '',
              promo_media_url: editingOffer?.promo_media_url || ''
            };
            console.log('Saving offer payload:', payload);
            saveOffer(payload);
          }} className="space-y-4">
            <div>
              <Label>Judul Offer</Label>
              <Input
                name="title"
                value={editingOffer?.title || ''}
                onChange={(e) => setEditingOffer(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Weekend Escape"
                required
              />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea
                name="description"
                value={editingOffer?.description || ''}
                onChange={(e) => setEditingOffer(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Get 25% off..."
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Kode Promo</Label>
              <Input
                name="code"
                value={editingOffer?.code || ''}
                onChange={(e) => setEditingOffer(prev => ({ ...prev, code: e.target.value }))}
                placeholder="WEEKEND25"
                required
              />
            </div>
            <div>
              <Label>Gambar Offer</Label>
              <div className="flex gap-2">
                <Input
                  name="image"
                  value={editingOffer?.image || ''}
                  onChange={(e) => setEditingOffer(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUploadTarget({ context: 'offer', mediaType: 'image' });
                    setShowMediaUpload(true);
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Pilih
                </Button>
              </div>
              {editingOffer?.image && (
                <div className="mt-2">
                  <img src={editingOffer.image} alt="Preview" className="w-full h-40 object-cover rounded-lg border" />
                </div>
              )}
            </div>
            <div>
              <Label>Berlaku Sampai</Label>
              <Input
                name="validUntil"
                value={editingOffer?.validUntil || ''}
                onChange={(e) => setEditingOffer(prev => ({ ...prev, validUntil: e.target.value }))}
                placeholder="31 Jan 2026"
                required
              />
            </div>
            <div>
              <Label>Image / Video Promo (Full Screen Overlay)</Label>
              <div className="flex gap-2">
                <Input
                  value={editingOffer?.promo_media_url || ''}
                  onChange={(e) => setEditingOffer(prev => ({ ...prev, promo_media_url: e.target.value }))}
                  placeholder="https://... (gambar atau video)"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUploadTarget({ context: 'offer_promo', mediaType: 'image' });
                    setShowMediaUpload(true);
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Pilih
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">URL gambar/video yang ditampilkan overlay saat pengunjung klik offer di Home. Kosongkan jika tidak ingin bisa diklik.</p>
              {editingOffer?.promo_media_url && (
                <div className="mt-2">
                  {editingOffer.promo_media_url.match(/\.(mp4|webm|ogg)$/i) || editingOffer.promo_media_url.includes('youtube') || editingOffer.promo_media_url.includes('youtu.be') ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-600 font-medium">🎬 Video promo aktif</span>
                      <Button variant="outline" size="sm" onClick={() => setEditingOffer(prev => ({ ...prev, promo_media_url: '' }))}>
                        <Trash2 className="w-3 h-3 mr-1" />Hapus
                      </Button>
                    </div>
                  ) : (
                    <>
                      <img src={editingOffer.promo_media_url} alt="Promo Preview" className="w-full h-32 object-cover rounded-lg border" />
                      <Button variant="outline" size="sm" className="mt-1" onClick={() => setEditingOffer(prev => ({ ...prev, promo_media_url: '' }))}>
                        <Trash2 className="w-3 h-3 mr-1" />Hapus
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowOfferModal(false)}>Batal</Button>
              <Button type="submit" disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />{isSaving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Facility Edit Modal */}
      <Dialog open={showFacilityModal} onOpenChange={(v) => { setShowFacilityModal(v); if (!v) setEditingFacility(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFacility?.id ? 'Edit Fasilitas' : 'Tambah Fasilitas'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            saveFacility({
              name: formData.get('name'),
              description: formData.get('description'),
              hours: formData.get('hours'),
              image: editingFacility?.image || formData.get('image'),
              is_active: formData.get('is_active') === 'on',
              order: parseInt(formData.get('order') || '0')
            });
          }} className="space-y-4">
            <div>
              <Label>Nama Fasilitas</Label>
              <Input
                name="name"
                defaultValue={editingFacility?.name || ''}
                placeholder="Infinity Pool"
                required
              />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea
                name="description"
                defaultValue={editingFacility?.description || ''}
                placeholder="Deskripsi fasilitas..."
                rows={3}
              />
            </div>
            <div>
              <Label>Jam Operasional / Info</Label>
              <Input
                name="hours"
                defaultValue={editingFacility?.hours || ''}
                placeholder="06:00 - 22:00"
              />
            </div>
            <div>
              <Label>Gambar Fasilitas</Label>
              <div className="flex gap-2">
                <Input
                  name="image"
                  value={editingFacility?.image || ''}
                  onChange={(e) => setEditingFacility(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setUploadTarget({ context: 'facility', mediaType: 'image' });
                    setShowMediaUpload(true);
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Pilih
                </Button>
              </div>
              {editingFacility?.image && (
                <div className="mt-2">
                  <img src={editingFacility?.image} alt="Preview" className="w-full h-40 object-cover rounded-lg border" />
                </div>
              )}
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Urutan</Label>
                <Input
                  type="number"
                  name="order"
                  defaultValue={editingFacility?.order || 0}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  name="is_active"
                  defaultChecked={editingFacility?.is_active ?? true}
                />
                <Label>Aktif</Label>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowFacilityModal(false)}>Batal</Button>
              <Button type="submit" disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" />{isSaving ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManagement;
